import html2canvas from 'html2canvas';

// html2canvas struggles with elements nested inside a CSS `transform: scale(...)`
// ancestor (used by ScaleToFit to shrink fixed-size cards on narrow screens) —
// it can miscompute text/element positions, producing garbled/overlapping output.
// Fix: clone the card into an off-screen, untransformed copy and capture that
// instead. The visible, scaled-down card on screen is never touched, so there's
// no flash/resize while downloading — the file is always saved at full,
// un-downscaled resolution regardless of how small the card is currently shown.
export async function captureCard(element, options = {}) {
  // Read the card's natural (unscaled) layout size BEFORE cloning — offsetWidth/
  // offsetHeight ignore CSS transforms, so this is correct even while the card
  // is currently displayed shrunk down. The clone needs this set explicitly:
  // its own `width:'100%'` would otherwise resolve against the viewport once
  // it's repositioned with `position:fixed`, not against the original 980px
  // design width, causing internal percentage-based layout (QR column, text
  // column) to collapse on narrow screens.
  const naturalWidth = element.offsetWidth;
  const naturalHeight = element.offsetHeight;

  const clone = element.cloneNode(true);
  clone.removeAttribute('id');
  clone.style.transform = 'none';
  clone.style.position = 'fixed';
  clone.style.top = '0';
  clone.style.left = '-99999px';
  clone.style.margin = '0';
  clone.style.width = `${naturalWidth}px`;
  clone.style.height = `${naturalHeight}px`;
  clone.style.maxWidth = 'none';

  // <canvas> content (e.g. QRCodeCanvas) isn't copied by cloneNode — redraw it.
  const originalCanvases = element.querySelectorAll('canvas');
  const clonedCanvases = clone.querySelectorAll('canvas');
  originalCanvases.forEach((orig, i) => {
    const target = clonedCanvases[i];
    if (target) target.getContext('2d').drawImage(orig, 0, 0);
  });

  document.body.appendChild(clone);
  try {
    return await html2canvas(clone, { scale: 2, backgroundColor: null, useCORS: true, ...options });
  } finally {
    document.body.removeChild(clone);
  }
}
